import { useState } from "react";
import { Loader2, Trash2, UserPlus } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, fmtDate, useApiData } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { TextField } from "@/components/common/Forms";
import { PageTitle } from "@/components/shell/DashboardShell";

const INIT = { name: "", email: "", password: "" };

const AddAdminForm = ({ onAdded }) => {
  const [f, setF] = useState(INIT);
  const [busy, setBusy] = useState(false);
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/team", f);
      toast.success(`${f.email} added as admin`);
      setF(INIT);
      onAdded();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="card-base space-y-4 p-6" data-testid="add-admin-form">
      <p className="flex items-center gap-2 font-semibold text-slate-900"><UserPlus className="h-4 w-4 text-[#C20500]" /> Add team member</p>
      <TextField id="admin-name" label="Full name" value={f.name} onChange={set("name")} required />
      <TextField id="admin-email" type="email" label="Work email (login ID)" value={f.email} onChange={set("email")} required />
      <TextField id="admin-password" type="password" label="Temporary password (min 8)" value={f.password} onChange={set("password")} required minLength={8} hint="Share it securely. They can change it via 'Forgot password'." />
      <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="add-admin-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Add admin</button>
    </form>
  );
};

export default function AdminTeam() {
  const { user } = useAuth();
  const { data, reload } = useApiData("/admin/team");
  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.email} from the admin team?`)) return;
    try {
      await api.delete(`/admin/team/${u.id}`);
      toast.success(`${u.email} removed`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };
  return (
    <div data-testid="admin-team-page">
      <PageTitle eyebrow="Access" title="Admin team" text="Everyone here can approve vendors, manage enquiries and update requests." />
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="card-base overflow-x-auto lg:col-span-2" data-testid="admin-team-table">
          <Table>
            <TableHeader><TableRow><TableHead>Name</TableHead><TableHead>Email</TableHead><TableHead>Added</TableHead><TableHead>Role</TableHead><TableHead /></TableRow></TableHeader>
            <TableBody>
              {(data || []).map((u) => (
                <TableRow key={u.id} data-testid={`admin-row-${u.email}`}>
                  <TableCell className="font-medium">{u.contact_name}</TableCell>
                  <TableCell className="text-sm">{u.email}</TableCell>
                  <TableCell className="text-sm text-slate-500">{fmtDate(u.created_at)}</TableCell>
                  <TableCell><span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${u.is_owner ? "bg-[#123634] text-white" : "bg-slate-100 text-slate-700"}`}>{u.is_owner ? "Owner" : "Admin"}</span></TableCell>
                  <TableCell className="text-right">
                    {!u.is_owner && u.id !== user.id && (
                      <button onClick={() => remove(u)} className="rounded p-1.5 text-slate-400 hover:bg-red-50 hover:text-red-600" aria-label="Remove admin" data-testid={`remove-admin-${u.email}`}><Trash2 className="h-4 w-4" /></button>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <AddAdminForm onAdded={reload} />
      </div>
    </div>
  );
}
